import React, { useState, useEffect, useMemo } from "react";
import {
  useGetGroupsQuery,
  useCreateSessionGroupMutation,
} from "../../../../services";

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  themeBox: boolean;
  blockId: string;
  sessionData?: any;
  categoryId: string;
  sessionFormatId: string;
  difficulty?: "BEGINNER" | "ADVANCED" | "GENERAL";
}

interface SetItem {
  id: string;
  name: string;
  setNumber: number;
  metrics?: Record<string, number>;
  // snapshot of selected exercise ids to avoid duplicates
  blocks?: string[];
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  themeBox,
  blockId,
  sessionData,
  categoryId,
  sessionFormatId,
  difficulty,
}) => {
  const [groupName, setGroupName] = useState("Superset");
  const [setCount, setSetCount] = useState(0);
  const [restTime, setRestTime] = useState("00:00:00");
  const {
    data: rawGroupsData,
    isFetching,
    isError,
  } = useGetGroupsQuery(categoryId, {
    skip: !isOpen || !categoryId,
    refetchOnMountOrArgChange: true,
  });

  const groupsData = useMemo(() => {
    if (Array.isArray(rawGroupsData)) return rawGroupsData;
    if (Array.isArray(rawGroupsData?.results)) return rawGroupsData.results;
    if (Array.isArray(rawGroupsData?.result)) return rawGroupsData.result;
    return [];
  }, [rawGroupsData]);

  const [sets, setSets] = useState<SetItem[]>(themeBox ? [] : []);
  const [selectedExercises, setSelectedExercises] = useState<string[]>([]);

  // Track metric values per selected group metric (_id -> value)
  const [metricValues, setMetricValues] = useState<Record<string, number>>({});

  // Helper to get selected group object
  const selectedGroup = useMemo(() => {
    const list = groupsData || [];
    return list.find((g: any) => g?.name === groupName) || null;
  }, [groupsData, groupName]);

  // Filter sessionData to show only SINGLE type exercises (exclude GROUP items)
  const flattenedExercises = useMemo(() => {
    if (!sessionData || !Array.isArray(sessionData)) return [];

    // Return each SINGLE block entry (use block _id) — do not dedupe by workout
    const list: Array<any> = [];
    sessionData.forEach((item: any) => {
      if (
        item.type === "SINGLE" &&
        item.workouts &&
        Array.isArray(item.workouts)
      ) {
        item.workouts.forEach((w: any) => {
          if (w.workout && w.workout._id) {
            list.push({
              blockId: item._id, // the block id the user expects
              workout: w.workout,
              name: w.workout.name,
            });
          }
        });
      }
    });

    return list;
  }, [sessionData]);

  // Extract GROUP type items for display in the right column
  const groupItems = useMemo(() => {
    if (!sessionData || !Array.isArray(sessionData)) return [];
    return sessionData.filter((item: any) => item.type === "GROUP");
  }, [sessionData]);

  // Local copy of groups for immediate UI updates (shows new groups without parent refresh)
  const [localGroups, setLocalGroups] = useState<any[]>([]);

  useEffect(() => {
    setLocalGroups(groupItems || []);
  }, [groupItems]);

  // Get exercise selection limit for the selected group (null/undefined => no limit)
  const groupExerciseLimit: number | null =
    selectedGroup && typeof selectedGroup.limit === "number"
      ? selectedGroup.limit
      : null;

  // Initialize metric values when groups load or selected group changes
  useEffect(() => {
    if (selectedGroup?.metrics && Array.isArray(selectedGroup.metrics)) {
      const init: Record<string, number> = {};
      selectedGroup.metrics.forEach((m: any) => {
        init[m?._id] = init[m?._id] ?? 1;
      });
      setMetricValues(init);
    } else {
      setMetricValues({});
    }
  }, [selectedGroup]);

  const handleRestTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    const digitsOnly = input.replace(/\D/g, "");

    if (digitsOnly.length > 6) {
      return;
    }

    const padded = digitsOnly.padEnd(6, "0").slice(0, 6);

    // Split into components
    const hours = padded.slice(0, 2);
    const minutes = Math.min(parseInt(padded.slice(2, 4), 10), 59)
      .toString()
      .padStart(2, "0");
    const seconds = Math.min(parseInt(padded.slice(4, 6), 10), 59)
      .toString()
      .padStart(2, "0");

    const formatted = `${hours}:${minutes}:${seconds}`;
    setRestTime(formatted);

    // Restore cursor position
    setTimeout(() => {
      let newPos = cursorPos;
      // Skip over colons
      if (cursorPos === 2 || cursorPos === 5) {
        newPos = cursorPos + 1;
      }
      e.target.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleRestTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const key = e.key;

    // Allow navigation and selection keys
    if (
      [
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Backspace",
        "Delete",
        "Home",
        "End",
      ].includes(key)
    ) {
      if (key === "ArrowRight" && cursorPos === 2) {
        e.preventDefault();
        input.setSelectionRange(3, 3);
      } else if (key === "ArrowRight" && cursorPos === 5) {
        e.preventDefault();
        input.setSelectionRange(6, 6);
      } else if (key === "ArrowLeft" && cursorPos === 3) {
        e.preventDefault();
        input.setSelectionRange(2, 2);
      } else if (key === "ArrowLeft" && cursorPos === 6) {
        e.preventDefault();
        input.setSelectionRange(5, 5);
      }
      return;
    }

    // Only allow digits
    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    // Skip colons
    let targetPos = cursorPos;
    if (targetPos === 2 || targetPos === 5) {
      targetPos++;
    }

    // Replace digit at cursor position
    const timeArray = restTime.split("");
    timeArray[targetPos] = key;
    const newTime = timeArray.join("");

    // Parse and validate
    const parts = newTime.split(":");
    const hours = parts[0];
    const minutes = Math.min(parseInt(parts[1], 10), 59)
      .toString()
      .padStart(2, "0");
    const seconds = Math.min(parseInt(parts[2], 10), 59)
      .toString()
      .padStart(2, "0");

    const formatted = `${hours}:${minutes}:${seconds}`;
    setRestTime(formatted);

    // Move cursor forward
    setTimeout(() => {
      let newPos = targetPos + 1;
      // Skip colons
      if (newPos === 2 || newPos === 5) {
        newPos++;
      }
      if (newPos > 7) newPos = 7;
      input.setSelectionRange(newPos, newPos);
    }, 0);
  };

  // Initialize selected exercises (start empty to force user to consciously select at least 2)
  useEffect(() => {
    setSelectedExercises([]);
  }, [flattenedExercises]);

  const toggleExercise = (exercise: any) => {
    const id = exercise?._id || exercise?.id || exercise;
    setSelectedExercises((prev) => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        return prev.filter((e) => e !== id);
      }
      if (groupExerciseLimit !== null && prev.length >= groupExerciseLimit) {
        return prev;
      }
      return [...prev, id];
    });
  };

  // Simple toast state
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(null), 2000);
    return () => clearTimeout(t);
  }, [toastMsg]);

  const addSet = () => {
    // Allow only one set in the list at a time
    if (sets.length >= 1) {
      setToastMsg("Only one group can be added at a time.");
      return;
    }
    // Snapshot current selected exercise ids
    const blocksSnapshot = selectedExerciseIds.slice().sort();

    // Enforce minimum 2 exercises
    if (blocksSnapshot.length < 2) {
      setToastMsg("Select at least two exercises to add this group.");
      return;
    }

    const uniqueId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const newSet: SetItem = {
      id: uniqueId,
      name: groupName,
      setNumber: setCount || 5,
      metrics: { ...metricValues },
      blocks: blocksSnapshot,
    };
    setSets((prev) => [...prev, newSet]);

    // Add a local display entry so user sees the new group immediately
    setLocalGroups((prev) => [
      ...prev,
      {
        _id: uniqueId,
        name: groupName,
        workouts: blocksSnapshot.map((b) => ({ _id: b })),
      },
    ]);
  };

  const removeSet = (id: string) => {
    setSets(sets.filter((set) => set.id !== id));
    setLocalGroups((prev) => prev.filter((g) => g._id !== id));
  };

  const decrementSet = () => {
    if (setCount > 0) setSetCount(setCount - 1);
  };

  const incrementSet = () => {
    setSetCount(setCount + 1);
  };

  // Default the selected group to first result when available
  useEffect(() => {
    const first = (groupsData as any)?.[0]?.name;
    if (first) setGroupName(first);
  }, [groupsData]);

  // Metric value handlers
  const decrementMetric = (metricId: string) => {
    setMetricValues((prev) => ({
      ...prev,
      [metricId]: Math.max(0, (prev[metricId] ?? 0) - 1),
    }));
  };
  const incrementMetric = (metricId: string) => {
    setMetricValues((prev) => ({
      ...prev,
      [metricId]: (prev[metricId] ?? 0) + 1,
    }));
  };

  // RTK mutation to submit group selection
  const [createSessionGroup, { isLoading: isCreating }] =
    useCreateSessionGroupMutation();

  // Helper: convert "HH:MM:SS" to seconds
  const timeToSeconds = (t: string) => {
    const [hh, mm, ss] = t.split(":").map((n) => parseInt(n || "0", 10));
    return (hh || 0) * 3600 + (mm || 0) * 60 + (ss || 0);
  };

  // Build matrix array from metricValues
  const matrics = useMemo(
    () =>
      Object.entries(metricValues).map(([metricId, value]) => ({
        metric: metricId,
        value: Number(value) || 0,
      })),
    [metricValues]
  );

  // Collect selected exercise ids from sessionData based on selection
  const selectedExerciseIds = useMemo(() => {
    return selectedExercises.filter((id: any) => typeof id === "string");
  }, [selectedExercises]);

  // helper: count metrics array length in a set snapshot
  const getMetricsCount = (s: SetItem) => {
    const src = s.metrics ?? {};
    return Object.keys(src).length;
  };

  // Submit handler
  const handleCreateGroup = async () => {
    if (!selectedGroup?._id) return;

    // Validate each set has at least two exercises
    const hasInvalidSet = sets.some((s) => (s.blocks?.length ?? 0) < 2);
    if (hasInvalidSet) {
      setToastMsg("Each group must contain at least two exercises.");
      return;
    }

    const groups = sets.map((set) => {
      const metricSrc = set.metrics ?? metricValues;
      const metrics = Object.entries(metricSrc).map(([metricId, value]) => ({
        metric: metricId,
        value: Number(value) || 0,
        static: false,
      }));

      return {
        blocks: set.blocks || [],
        group: selectedGroup._id,
        difficulty: difficulty || "GENERAL",
        metrics,
        restTime: timeToSeconds(restTime),
      };
    });

    const payload = { sessionFormatId, groups };

    try {
      await createSessionGroup(payload).unwrap();
      onClose();
    } catch (err) {
      console.error("Failed to create group selection", err);
    }
  };

  if (!isOpen) return null;

  // Theme styles
  const overlayBg = themeBox ? "bg-black/70" : "bg-white/70";
  const modalBg = themeBox
    ? "bg-[#1A1A1A] border-[#2C2C2C]"
    : "bg-white border-[#E5E7EB]";
  const headerText = themeBox ? "text-white" : "text-gray-900";
  const inputBg = themeBox
    ? "bg-[#0F0F0F] border-[#2C2C2C] text-white"
    : "bg-white border-[#E5E7EB] text-gray-900";
  const inputFocus = "focus:border-[#C6D92C]";
  const labelText = themeBox ? "text-gray-400" : "text-gray-500";
  const cancelBtnBg = themeBox
    ? "bg-[#0F0F0F] text-gray-300 hover:bg-[#1A1A1A]"
    : "bg-[#F3F3F6] text-gray-700 hover:bg-[#E5E7EB]";
  const exerciseBg = themeBox
    ? "bg-[#0F0F0F] border-[#2C2C2C]"
    : "bg-[#F3F3F6] border-[#E5E7EB]";
  const exerciseSelectedBg = themeBox ? "bg-[#1A1A1A]" : "bg-[#E5E7EB]";
  const setListBg = themeBox
    ? "bg-[#0F0F0F] border-[#2C2C2C]"
    : "bg-[#F3F3F6] border-[#E5E7EB]";
  const setItemBg = themeBox
    ? "bg-[#1A1A1A] border-[#2C2C2C]"
    : "bg-[#E5E7EB] border-[#E5E7EB]";
  const setText = themeBox ? "text-gray-200" : "text-gray-900";
  const setLabelText = themeBox ? "text-gray-400" : "text-gray-500";
  const svgColor = "#C6D92C";
  const svgStroke = themeBox ? "#F3F3F6" : "#222";

  return (
    <div
      className={`fixed inset-0 ${overlayBg} flex items-center justify-center z-50 backdrop-blur-sm`}
    >
      {/* Inline toast */}
      {toastMsg && (
        <div
          className={`fixed top-4 right-4 z-[60] px-3 py-2 rounded text-[12px] shadow ${themeBox
            ? "bg-[#1A1A1A] text-white border border-[#2C2C2C]"
            : "bg-white text-gray-900 border border-[#E5E7EB]"
            }`}
        >
          {toastMsg}
        </div>
      )}
      <div className={`w-[900px] rounded-lg ${modalBg} border`}>
        {/* Header */}
        <div
          className={`flex items-center justify-center relative px-5 py-3.5 border-b ${themeBox ? "border-[#2C2C2C]" : "border-[#E5E7EB]"
            }`}
        >
          <h2
            className={`text-[15px] font-semibold ${headerText} text-center w-full`}
          >
            Create By Group
          </h2>
          <button
            onClick={onClose}
            className={`absolute right-5 p-1 ${themeBox
              ? "text-gray-400 hover:text-white"
              : "text-gray-400 hover:text-gray-900"
              } transition-colors`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={themeBox ? "currentColor" : "#222"}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body - 3 Column Layout */}
        <div className="p-5">
          <div className="grid grid-cols-3 gap-4">
            {/* Column 1 - Exercise List */}
            <div className="col-span-1">
              <div
                className={`rounded-lg ${exerciseBg} p-2.5 h-[400px] overflow-y-auto custom-scrollbar`}
              >
                <div className="space-y-0.5">
                  {flattenedExercises.map((exercise: any) => {
                    const exId = exercise.blockId;
                    const isChecked = selectedExercises.includes(exId);
                    const atLimit =
                      groupExerciseLimit !== null &&
                      selectedExercises.length >= groupExerciseLimit;
                    const disableCheckbox = !isChecked && atLimit; // disable only for new selections at limit

                    return (
                      <label
                        key={exercise.blockId}
                        className={`flex items-center gap-2.5 py-2 px-2.5 rounded cursor-pointer transition-colors ${isChecked ? exerciseSelectedBg : themeBox
                          } ${disableCheckbox ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleExercise(exercise.blockId)}
                          disabled={disableCheckbox}
                          className={`w-3.5 h-3.5 rounded ${themeBox
                            ? "border-[#E5E7EB] bg-white accent-[#C6D92C]"
                            : "border-[#2C2C2C] bg-[#0F0F0F] accent-[#C6D92C]"
                            } cursor-pointer`}
                        />
                        <span
                          className={`text-[12px] ${themeBox ? "text-gray-400" : "text-gray-800"
                            } flex-1`}
                        >
                          {exercise.workout?.name || exercise.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {/* Optional hint when at limit */}
                {groupExerciseLimit !== null &&
                  selectedExercises.length >= groupExerciseLimit && (
                    <div
                      className={`mt-2 text-[11px] ${themeBox ? "text-gray-500" : "text-gray-600"
                        }`}
                    >
                      Selection limit reached ({groupExerciseLimit}).
                    </div>
                  )}
              </div>
            </div>

            {/* Column 2 - Group Settings */}
            <div className="col-span-1 space-y-3.5">
              {/* Group Name */}
              <div>
                <label className={`block text-[11px] ${labelText} mb-1.5`}>
                  Group Name
                </label>
                <div className="relative">
                  <select
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    className={`w-full px-3 py-2 rounded border ${themeBox ? "border-[#2C2C2C]" : "border-[#E5E7EB]"
                      } ${inputBg} text-[12px] outline-none appearance-none cursor-pointer`}
                  >
                    {isFetching && <option>Loading groups...</option>}
                    {isError && <option>Failed to load groups</option>}
                    {!isFetching && !isError && groupsData?.length ? (
                      groupsData.map((g: any) => (
                        <option key={g._id} value={g.name}>
                          {g.name}
                        </option>
                      ))
                    ) : (
                      <></>
                    )}
                  </select>
                  <svg
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Metrics (replaces Set) */}
              <div>
                {/* When loading/error/fallback */}
                {isFetching && (
                  <div
                    className={`text-[12px] ${themeBox ? "text-gray-400" : "text-gray-600"
                      }`}
                  >
                    Loading groups...
                  </div>
                )}
                {isError && (
                  <div className="text-[12px] text-red-500">
                    Failed to load groups
                  </div>
                )}

                {!isFetching && !isError && selectedGroup?.metrics?.length ? (
                  <div className="space-y-2 mt-2">
                    {selectedGroup.metrics.map((m: any) => (
                      <div key={m._id}>
                        <div className={`text-[11px] ${labelText} mb-1`}>
                          {m.name}
                        </div>
                        <div className="flex items-center justify-center gap-0 w-full">
                          <button
                            type="button"
                            onClick={() => decrementMetric(m._id)}
                            className={`w-12 h-10 rounded-l-[10px] border ${themeBox
                              ? "border-[#2C2C2C] bg-[#0F0F0F] text-gray-300 hover:bg-[#1A1A1A]"
                              : "border-[#E5E7EB] bg-white text-gray-700 hover:bg-[#E5E7EB]"
                              } flex items-center justify-center`}
                            style={{ borderRight: "none" }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                            >
                              <circle
                                cx="9"
                                cy="9"
                                r="8.5"
                                stroke="currentColor"
                                strokeWidth="1"
                              />
                              <path
                                d="M6 9H12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                          <input
                            type="text"
                            value={(metricValues[m._id] ?? 1)
                              .toString()
                              .padStart(2, "0")}
                            readOnly
                            className={`h-10 text-[14px] font-semibold ${themeBox
                              ? "text-white bg-[#0F0F0F] border-[#2C2C2C]"
                              : "text-gray-900 bg-white border-[#E5E7EB]"
                              } text-center border-t border-b outline-none`}
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              borderLeft: "none",
                              borderRight: "none",
                              borderRadius: 0,
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => incrementMetric(m._id)}
                            className={`w-12 h-10 rounded-r-[10px] border ${themeBox
                              ? "border-[#2C2C2C] bg-[#0F0F0F] text-gray-300 hover:bg-[#1A1A1A]"
                              : "border-[#E5E7EB] bg-white text-gray-700 hover:bg-[#E5E7EB]"
                              } flex items-center justify-center`}
                            style={{ borderLeft: "none" }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                            >
                              <circle
                                cx="9"
                                cy="9"
                                r="8.5"
                                stroke="currentColor"
                                strokeWidth="1"
                              />
                              <path
                                d="M9 6V12M6 9H12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`text-[12px] mt-2 ${themeBox ? "text-gray-400" : "text-gray-600"
                      }`}
                  >
                    No metrics available for the selected group.
                  </div>
                )}
              </div>

              {/* Rest Time */}
              <div>
                <label className={`block text-[11px] ${labelText} mb-1.5`}>
                  Rest Time
                </label>
                <input
                  type="text"
                  value={restTime}
                  onChange={handleRestTimeChange}
                  onKeyDown={handleRestTimeKeyDown}
                  placeholder="00:00:00"
                  className={`w-full px-3 py-2 rounded border ${themeBox
                    ? "border-[#2C2C2C] bg-[#0F0F0F] text-white"
                    : "border-[#E5E7EB] bg-white text-gray-900"
                    } text-[12px] text-center outline-none ${inputFocus}`}
                />
              </div>

              <button
                type="button"
                onClick={addSet}
                disabled={sets.length >= 1 || selectedExerciseIds.length < 2}
                className={`w-2/4 py-2 text-[12px] font-medium flex items-center justify-center gap-4 transition-opacity hover:opacity-90 rounded-[10px] border mx-auto ${sets.length >= 1 || selectedExerciseIds.length < 2
                  ? "opacity-60 cursor-not-allowed"
                  : ""
                  }`}
                style={{
                  borderColor: "#C6D92C",
                  background: themeBox
                    ? "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)"
                    : "linear-gradient(94.17deg, rgba(198, 217, 44, 0.80) 0%, rgba(243, 243, 246, 0.80) 100%)",
                  color: themeBox ? "#fff" : "#222",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11 6H21"
                    stroke={svgStroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M11 12H21"
                    stroke={svgStroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M11 18H21"
                    stroke={svgStroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M3 7.39286C3 7.39286 4 8.04466 4.5 9C4.5 9 6 5.25 8 4"
                    stroke={svgStroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 18.3929C3 18.3929 4 19.0447 4.5 20C4.5 20 6 16.25 8 15"
                    stroke={svgStroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add to List
              </button>
              {selectedExerciseIds.length < 2 && sets.length === 0 && (
                <p className={`text-[11px] text-center mt-2 ${themeBox ? "text-red-400" : "text-red-500"}`}>
                  Please select at least two exercises to create a group.
                </p>
              )}
            </div>

            {/* Column 3 - Group Items List */}
            <div className="col-span-1">
              <div
                className={`rounded-lg border ${setListBg} p-2.5 h-[400px] overflow-y-auto custom-scrollbar`}
              >
                <div className="space-y-1.5">
                  {groupItems.length > 0 || sets.length > 0 ? (
                    <>
                      {groupItems.map((group: any) => {
                        const workoutCount = group.workouts?.length || 0;
                        return (
                          <div
                            key={group._id}
                            className={`flex items-center justify-between p-2.5 rounded ${setItemBg}`}
                          >
                            <div className="flex items-center gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M8 6H8.00635M8 12H8.00635M8 18H8.00635M15.9937 6H16M15.9937 12H16M15.9937 18H16"
                                  stroke="#949494"
                                  stroke-width="2.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                              <span className={`text-[12px] ${setText}`}>
                                {group.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[11px] ${setLabelText}`}
                                title="Workout count"
                              >
                                {workoutCount}{" "}
                                {workoutCount === 1 ? "workout" : "workouts"}
                              </span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                              >
                                <path
                                  d="M12.75 0.75L0.75081 12.7492M12.7492 12.75L0.75 0.75085"
                                  stroke="#F3F3F6"
                                  stroke-width="1.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                        );
                      })}

                      {sets.map((set: any) => {
                        const workoutCount = (set.blocks || []).length || 0;
                        return (
                          <div
                            key={set.id}
                            className={`flex items-center justify-between p-2.5 rounded ${setItemBg}`}
                          >
                            <div className="flex items-center gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M8 6H8.00635M8 12H8.00635M8 18H8.00635M15.9937 6H16M15.9937 12H16M15.9937 18H16"
                                  stroke="#949494"
                                  stroke-width="2.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                              <span className={`text-[12px] ${setText}`}>
                                {set.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[11px] ${setLabelText}`}
                                title="Workout count"
                              >
                                {workoutCount}{" "}
                                {workoutCount === 1 ? "workout" : "workouts"}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSet(set.id)}
                                className="p-1 rounded hover:bg-red-600/10"
                                title="Remove"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="#F3F3F6"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div
                      className={`text-[12px] ${themeBox ? "text-gray-400" : "text-gray-600"
                        } text-center py-4`}
                    >
                      No groups available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-center gap-3 px-5 py-3.5 border-t ${themeBox ? "border-[#2C2C2C]" : "border-[#E5E7EB]"
            }`}
        >
          <button
            className={`px-4 py-2 rounded text-[12px] font-medium transition-opacity hover:opacity-90 flex items-center justify-center gap-4`}
            style={{
              borderColor: "#C6D92C",
              background: themeBox
                ? "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)"
                : "linear-gradient(94.17deg, rgba(198, 217, 44, 0.80) 0%, rgba(243, 243, 246, 0.80) 100%)",
              color: themeBox ? "#fff" : "#222",
            }}
            onClick={handleCreateGroup}
            disabled={
              isCreating || !selectedGroup?._id || sets.length === 0 // enable once items are added to the list
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M8 6.99446H12.0179M12.0179 6.99446H20C21.1046 6.99446 22 7.88989 22 8.99446V19C22 20.1046 21.1046 21 20 21H4C2.89543 21 2 20.1046 2 19V4C2 3.44772 2.44772 3 3 3H8.5022C8.81572 3 9.11109 3.14703 9.30008 3.39718L12.0179 6.99446Z"
                stroke={svgStroke}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isCreating ? "Creating..." : "Create Group"}
          </button>
          <button
            onClick={onClose}
            className={`px-8 py-3 rounded text-[12px] font-medium ${cancelBtnBg} transition-colors`}
          >
            Cancel
          </button>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${themeBox ? "#0F0F0F" : "#F3F3F6"};
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${themeBox ? "#2C2C2C" : "#E5E7EB"};
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${themeBox ? "#3C3C3C" : "#C6D92C"};
        }
      `}</style>
    </div>
  );
};

export default CreateGroupModal;
